// =============================================================================
// ui/state/exitModels.tsx — the exit-model catalogue.
// -----------------------------------------------------------------------------
// Loaded once at the shell, beside patterns. Same shape, same reasoning:
//
// ⚠ NULL until a read succeeds. An empty catalogue is a legitimate state — a user
// who has not written any exit models — but a FAILED read is not, and
// `io/sync/exitModels.ts` throws so this provider never has to guess which of the
// two it is holding. The trade form shows different copy for each, and showing
// "you have no exit models yet" after a failed read would send someone off to
// create rules they already have.
// =============================================================================

import {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
  type ReactNode,
} from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import { loadExitModels, type ExitModelSummary } from '../../io/sync/exitModels';
import { reportFailure } from '../../io/diagnostics/errorLog';

export interface ExitModelsState {
  phase: 'loading' | 'ready' | 'failed';
  exitModels: readonly ExitModelSummary[] | null;
  error: string | null;
  reload: () => void;
}

const Ctx = createContext<ExitModelsState | null>(null);

export function useExitModels(): ExitModelsState {
  const v = useContext(Ctx);
  if (!v) throw new Error('useExitModels() outside <ExitModelsProvider>');
  return v;
}

export function ExitModelsProvider({ sb, children }: { sb: SupabaseClient; children: ReactNode }) {
  const [phase, setPhase] = useState<ExitModelsState['phase']>('loading');
  const [exitModels, setExitModels] = useState<readonly ExitModelSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setPhase('loading');
    setError(null);
    void (async () => {
      try {
        const m = await loadExitModels(sb);
        if (cancelled) return;
        setExitModels(m);
        setPhase('ready');
      } catch (e) {
        if (cancelled) return;
        setError(reportFailure('exit models', e));
        setPhase('failed');
      }
    })();
    return () => { cancelled = true; };
  }, [sb, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  const value = useMemo<ExitModelsState>(
    () => ({ phase, exitModels, error, reload }), [phase, exitModels, error, reload],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
